import { createRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { authenticatedRoute } from '@/authenticated-route';
import FullPageLoadingSpinner from '@/components/ui/full-page-loading-spinner';
import { PATIENT_360_ROUTES } from '@/features/patient-360/consts/routes';
import { inpatientInfoRoutes } from '@/features/patient-360/inpatient-info-routes';
import { patient360ViewRoute } from '@/features/patient-360/patient-360-view-route';
import { reportRoutes } from '@/features/patient-360/report-routes';
import { sharedSearchParams } from '@/features/patient-360/types/route-schema';
import { vitalSignsRoutes } from '@/features/patient-360/vital-signs-routes';
import { beforeLoadRouterGuard } from '@/integrations/tanstack-router/before-load-router-guard';
import { relativeTo } from '@/integrations/tanstack-router/relative-to';

export type SharedSearchParams = z.infer<typeof sharedSearchParams>;
export type SharedSearchDateRange = Pick<
  SharedSearchParams,
  'preset' | 'startDate' | 'endDate'
>;

const P360_VIEW_CHILD_STATIC_DATA = {
  home: PATIENT_360_ROUTES.patientView,
  parent: PATIENT_360_ROUTES.patientView,
} as const;

const visitsRoute = createRoute({
  getParentRoute: () => patient360ViewRoute,
  path: relativeTo(PATIENT_360_ROUTES.visits, PATIENT_360_ROUTES.patientView),
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() => import('./components/visits/visits').then(({ Route }) => Route));

const outpatientInfoRoute = createRoute({
  getParentRoute: () => patient360ViewRoute,
  path: relativeTo(
    PATIENT_360_ROUTES.outpatientInfo,
    PATIENT_360_ROUTES.patientView,
  ),
  staticData: {
    home: PATIENT_360_ROUTES.patientView,
    parent: PATIENT_360_ROUTES.visits,
  },
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(
    sharedSearchParams.extend({ encounterId: z.string().optional() }),
  ),
}).lazy(() =>
  import('./components/outpatient-info/outpatient-info').then(
    ({ Route }) => Route,
  ),
);

const vaccinationsRoute = createRoute({
  getParentRoute: () => patient360ViewRoute,
  path: relativeTo(
    PATIENT_360_ROUTES.vaccinations,
    PATIENT_360_ROUTES.patientView,
  ),
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(
    sharedSearchParams.extend({ mrn: z.string().min(1) }),
  ),
}).lazy(() =>
  import('./components/vaccinations/vaccinations').then(({ Route }) => Route),
);

const diagnosisCertificatesRoute = createRoute({
  getParentRoute: () => patient360ViewRoute,
  path: relativeTo(
    PATIENT_360_ROUTES.diagnosisCertificates,
    PATIENT_360_ROUTES.patientView,
  ),
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() =>
  import('./components/diagnosis-certificates').then(({ Route }) => Route),
);

const medicationsRoute = createRoute({
  getParentRoute: () => patient360ViewRoute,
  path: relativeTo(
    PATIENT_360_ROUTES.medications,
    PATIENT_360_ROUTES.patientView,
  ),
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() => import('./components/medications').then(({ Route }) => Route));

const chronicDiseaseRoute = createRoute({
  getParentRoute: () => patient360ViewRoute,
  path: relativeTo(
    PATIENT_360_ROUTES.chronicDisease,
    PATIENT_360_ROUTES.patientView,
  ),
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() =>
  import('./components/chronic-disease').then(({ Route }) => Route),
);

const cloudPharmacyRoute = createRoute({
  getParentRoute: () => patient360ViewRoute,
  path: relativeTo(
    PATIENT_360_ROUTES.cloudPharmacy,
    PATIENT_360_ROUTES.patientView,
  ),
  staticData: P360_VIEW_CHILD_STATIC_DATA,
  beforeLoad: beforeLoadRouterGuard([]),
  validateSearch: zodValidator(sharedSearchParams),
}).lazy(() => import('./components/cloud-pharmacy').then(({ Route }) => Route));

const patient360IndexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: PATIENT_360_ROUTES.index,
  staticData: {
    parent: '/',
    home: null,
  },
  pendingComponent: FullPageLoadingSpinner,
}).lazy(() => import('./patient-360').then(({ Route }) => Route));

const patient360ViewRouteTree = patient360ViewRoute.addChildren([
  visitsRoute,
  reportRoutes,
  inpatientInfoRoutes,
  outpatientInfoRoute,
  vaccinationsRoute,
  vitalSignsRoutes,
  diagnosisCertificatesRoute,
  medicationsRoute,
  chronicDiseaseRoute,
  cloudPharmacyRoute,
]);

export const patient360Routes = [
  patient360IndexRoute,
  patient360ViewRouteTree,
] as const;
